import moment from 'moment';
import { stepChangesToYourJourneyTrawickLookup as expenseLookup } from './claimCodeMapper';
import { makeSelectClaimTypes, makeSelectClaimList } from 'containers/ClaimList/selectors';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire(data, claimCode) {
  const questionnaire = [];
  const entries = Object.entries(data);
  const excludedEntries = ['lossInformationItemsTravelCancellation',
    'reasonForTripCancellation',
    'insuredsTravelCancellation',
    'travelSuppliersTripCancellationForAnyReason',
    'lossInformationItemsTripCancellationForAnyReason',
    'insuredsTripCancellationForAnyReason',
    'lossInformationItems',
    'insuredsForInterruption',
    'reasonsForTripInterruption',
    'travelSuppliersTravelInterruption',
    'delayInformationItems',
    'selectedClaimFor',
    'reasonsForDelay',
    'reasonsForMissedConnection',
    'missedConnectionInformationItems',

    'flightDetailsTravelCancellation',
    'flightDetailsTravelInterruption',
    'flightDetailsTravelDelay',
    'flightDetailsMissedConnection',

    'cruiseLineDetailsTravelCancellation',
    'cruiseLineDetailsTravelInterruption',
    'cruiseLineDetailsTravelDelay',
    'cruiseLineDetailsMissedConnection',

    'otherDetailsTravelCancellation',
    'otherDetailsTravelInterruption',
    'otherDetailsTravelDelay',
    'otherDetailsMissedConnection',

    'chooseAnInsured',
    'chooseAnInsuredTI',
  ];
  entries.forEach(([property, value]) => {
    if (excludedEntries.indexOf(property) < 0) {
      questionnaire.push({
        question: `changesToYourJourneyTrawick.${property}`,
        answer: value,
        claimTypeCode: claimCode,
      });
    }
  });
  return [...questionnaire];
}

function mapToExpenses(data) {
  const tripInterruptedExpenses = mapToTripInterruptionExpense(data);
  const tripCancellationExpenses = mapToTripCancellationExpense(data);
  const tripCancellationForReasonExpenses = mapToCancellationForReasonExpense(data);
  const tripDelayExpenses = mapToTripDelayExpense(data);
  const missedConnectionExpenses = mapToMissedConnectionExpense(data);
  const benefitName = 'Changes to Your Journey';
  const expenses = [
    ...tripInterruptedExpenses,
    ...tripCancellationExpenses,
    ...tripCancellationForReasonExpenses,
    ...tripDelayExpenses,
    ...missedConnectionExpenses,
  ].map((e) => ({ ...e, benefitName }));
  return expenses;
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const { tripCancellation, travelInterruption, travelDelay, missedConnection } = data;
  const list = [];
  const selected =
    !!selectedBenefits && typeof selectedBenefits.toJS === 'function'
      ? selectedBenefits.toJS()
      : selectedBenefits || [];
  const claimCodeList = expenseLookup.claimCodeList;
  if (selected.length > 0 && !selected.some((sel) => claimCodeList.includes(sel.claimCode))
  ) {
    return list;
  }
  if ((tripCancellation === 'true' || travelInterruption === 'true' || travelDelay === 'true' || missedConnection === 'true') && claimTypes !== undefined) {
    const code1 = expenseLookup.tripCancellation.claimCode;
    const code2 = expenseLookup.travelInterruption.claimCode;
    const code3 = expenseLookup.travelDelay.claimCode;
    const code4 = expenseLookup.missedConnection.claimCode;
    const tripCancellationExpenses = mapToTripCancellationExpense(data);
    const tripInterruptedExpenses = mapToTripInterruptionExpense(data);
    const tripDelayExpenses = mapToTripDelayExpense(data);
    const missedConnectionExpenses = mapToMissedConnectionExpense(data);
    claimTypes.forEach(
      (item) => {
        if (item.claimCode === code1 && tripCancellation === 'true') {
          const benefit = mapClaimExpensesLevelItem(item, 'Trip Cancellation', tripCancellationExpenses);
          if (!!benefit) {
            list.push(benefit);
          }
        }
        if (item.claimCode === code2 && travelInterruption === 'true') {
          const benefit = mapClaimExpensesLevelItem(item, 'Trip Interruption', tripInterruptedExpenses);
          if (!!benefit) {
            list.push(benefit);
          }
        }
        if (item.claimCode === code3 && travelDelay === 'true') {
          const benefit = mapClaimExpensesLevelItem(item, 'Travel Delay', tripDelayExpenses);
          if (!!benefit) {
            list.push(benefit);
          }
        }
        if (item.claimCode === code4 && missedConnection === 'true') {
          const benefit = mapClaimExpensesLevelItem(item, 'Missed Connections', missedConnectionExpenses);
          if (!!benefit) {
            list.push(benefit);
          }
        }
      }
    );
  }

  return list;
}

function mapToTripInterruptionExpense(data) {
  if (data.travelInterruption !== 'true' || data.lossInformationItems.length < 1) {
    return [];
  }
  const claimCode = expenseLookup.travelInterruption.claimCode;
  const expenses = [];

  data.lossInformationItems.forEach(
    (item) => {
      const expense = mapInterruptionItemExpense(item, data, claimCode);
      if (!!expense) {
        expenses.push(expense);
      }
      const reimburseExpense = mapReimbursement(item, data, claimCode);
      if (!!reimburseExpense) {
        expenses.push(reimburseExpense);
      }
    }
  );

  return expenses;
}

function mapToTripCancellationExpense(data) {
  if (data.tripCancellation !== 'true' || data.lossInformationItemsTravelCancellation.length < 1) {
    return [];
  }
  const isCFAR = data.reasonForTripCancellation.includes('None of the Above');
  const claimCode = isCFAR ? expenseLookup.tripCancellationForAnyReason.claimCode : expenseLookup.tripCancellation.claimCode;
  const expenses = [];

  data.lossInformationItemsTravelCancellation.forEach(
    (item) => {
      const expense = mapCancellationItemExpense(item, data, claimCode);
      if (!!expense) {
        expenses.push(expense);
      }
      const reimburseExpense = mapReimbursement(item, data, claimCode);
      if (!!reimburseExpense) {
        expenses.push(reimburseExpense);
      }
    }
  );

  return expenses;
}

function mapToCancellationForReasonExpense(data) {
  if (data.tripCancellationForAnyReason !== 'true' || data.lossInformationItemsTripCancellationForAnyReason.length < 1) {
    return [];
  }
  const claimCode = expenseLookup.tripCancellationForAnyReason.claimCode;
  const expenses = [];

  data.lossInformationItemsTripCancellationForAnyReason.forEach(
    (item) => {
      const expense = mapCancellationForReasonItemExpense(item, data, claimCode);
      if (!!expense) {
        expenses.push(expense);
      }
      const reimburseExpense = mapReimbursement(item, data, claimCode);
      if (!!reimburseExpense) {
        expenses.push(reimburseExpense);
      }
    }
  );

  return expenses;
}

function mapToTripDelayExpense(data) {
  if (data.travelDelay !== 'true' || data.delayInformationItems.length < 1) {
    return [];
  }
  const claimCode = expenseLookup.travelDelay.claimCode;
  const expenses = [];

  data.delayInformationItems.forEach(
    (item) => {
      const expense = mapDelayItemExpense(item, data, claimCode);
      if (!!expense) {
        expenses.push(expense);
      }
      const reimburseExpense = mapReimbursementTDL(item, data, claimCode);
      if (!!reimburseExpense) {
        expenses.push(reimburseExpense);
      }
    }
  );

  return expenses;
}

function mapToMissedConnectionExpense(data) {
  if (data.missedConnection !== 'true' || data.missedConnectionInformationItems.length < 1) {
    return [];
  }
  const claimCode = expenseLookup.missedConnection.claimCode;
  const expenses = [];

  data.missedConnectionInformationItems.forEach(
    (item) => {
      const expense = mapMissedConnectionItemExpense(item, data, claimCode);
      if (!!expense) {
        expenses.push(expense);
      }
      const reimburseExpense = mapReimbursementTDL(item, data, claimCode);
      if (!!reimburseExpense) {
        expenses.push(reimburseExpense);
      }
    }
  );

  return expenses;
}

function mapInterruptionItemExpense(item, formData, claimCode) {
  return mapExpenseItem(item, formData.dateTripInterrupted, claimCode);
}

function mapCancellationItemExpense(item, formData, claimCode) {
  return mapExpenseItem(item, formData.dateTravelCancellation, claimCode);
}

function mapCancellationForReasonItemExpense(item, formData, claimCode) {
  return mapExpenseItem(item, formData.dateTripCancellationForAnyReason, claimCode);
}

function mapDelayItemExpense(item, formData, claimCode) {
  return mapExpenseItemTDL(item, item.dateOfLoss, claimCode);
}

function mapMissedConnectionItemExpense(item, formData, claimCode) {
  return mapExpenseItemTDL(item, item.dateOfLoss, claimCode);
}

function mapExpenseItem(item, itemDate, claimCode) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountPaidLoss || !item.currentAmountPaidLoss) {
    return null;
  }
  let reimbursementReceived = 'N/A';
  if (item.howMuchReceivedReimbursement &&
    item.currentReceivedReimbursement &&
    item.haveYouReceivedReimbursement === 'true') {
    reimbursementReceived = `${item.currentReceivedReimbursement} ${item.howMuchReceivedReimbursement}`;
  }

  return {
    shortDescription: `${item.companyNameLoss}`,
    dateOfExpense: itemDate,
    clientCost: item.amountPaidLoss,
    currency: item.currentAmountPaidLoss,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    claimExpenseGuid: item.claimExpenseGuid,
    reimbursementReceived,
  };
}

function mapReimbursement(item, formData, claimCode) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.howMuchReceivedReimbursement || !item.currentReceivedReimbursement || item.haveYouReceivedReimbursement !== 'true') {
    return null;
  }
  return {
    shortDescription: `${item.fromWhomReceivedReimbursement} (Reimbursement)`,
    dateOfExpense: item.dateReimbursementReceived,
    clientCost: `${item.howMuchReceivedReimbursement * -1}`,
    currency: item.currentReceivedReimbursement,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    claimExpenseGuid: item.claimExpenseGuid,
  };
}

function mapExpenseItemTDL(item, itemDate, claimCode) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountPaidLoss || !item.currentAmountPaidLoss) {
    return null;
  }
  return {
    shortDescription: `${item.companyNameLoss}`,
    dateOfExpense: moment(),
    clientCost: item.amountPaidLoss,
    currency: item.currentAmountPaidLoss,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    claimExpenseGuid: item.claimExpenseGuid,
  };
}

function mapReimbursementTDL(item, formData, claimCode) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.howMuchReceivedReimbursement || !item.currentReceivedReimbursement || item.haveYouReceivedReimbursement !== 'true') {
    return null;
  }
  return {
    shortDescription: `${item.fromWhomReceivedReimbursement} (Reimbursement)`,
    dateOfExpense: moment(),
    clientCost: `${item.howMuchReceivedReimbursement * -1}`,
    currency: item.currentReceivedReimbursement,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    claimExpenseGuid: item.claimExpenseGuid,
  };
}

function mapClaimExpensesLevelItem(claimType, description, expenses) {
  return {
    claimCode: claimType.claimCode,
    description,
    claimTypeId: claimType.claimTypeId,
    requiredDocuments: claimType.requiredDocuments,
    order: null,
    expenses: expenses || [],
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepChangesToYourJourneyDataMapper.js